import moment from 'moment';
import { getOrderPrimaryPhaseLabel } from '../../charts/pipeline/lib/orderPrimaryPhaseConfig';
import { resolveAnchorQuickPickButtonLabel } from './periodQuickPickStaging';
import { STATISTICS_BASE_FILTER_DEFAULTS, buildStatisticsDefaultOrderDateFields } from './statisticsFilterParams';
import { formatComparePeriodLabel, trendAnchorMatchesOrderRange, resolveComparePeriodChipLabel } from './statisticsComparePresets';
import { normalizeCompareDateKey } from './statisticsCompareConfig';
import { buildReleaseOverviewPeriodToYearPatch } from './overviewPeriodUnit';

const formatMultiValueLabel = (values, max = 2) => {
	if (!values?.length) return '';
	if (values.length <= max) return values.join('、');
	return `${values.slice(0, max).join('、')} 等${values.length}项`;
};

export const isDefaultThisYearOrderRange = (startDate, endDate) => {
	const defaults = buildStatisticsDefaultOrderDateFields();
	return (
		normalizeCompareDateKey(startDate) === normalizeCompareDateKey(defaults.startDate) &&
		normalizeCompareDateKey(endDate) === normalizeCompareDateKey(defaults.endDate)
	);
};

const formatDateLabel = (value) => {
	if (!value) return '';
	const parsed = moment(value);
	return parsed.isValid() ? parsed.format('YYYY-MM-DD') : String(value);
};

const formatDateRangeLabel = (from, to) => {
	const friendly = from && to ? formatComparePeriodLabel(from, to) : '';
	if (friendly) return friendly;
	const start = formatDateLabel(from);
	const end = formatDateLabel(to);
	if (start && end) return start === end ? start : `${start} ~ ${end}`;
	if (start) return `${start} 起`;
	if (end) return `${end} 止`;
	return '';
};

/** 完成日期标签：与选期按钮一致，优先展示按月/按季/离散年份元数据 */
export const resolveStatisticsCompletedDateRangeLabel = (startDate, endDate, completedDateAnchor = null) => {
	if (!startDate && !endDate) return '';
	const anchor = completedDateAnchor;
	if (anchor?.startDate && anchor?.endDate && trendAnchorMatchesOrderRange(anchor, startDate, endDate)) {
		const quickPickLabel = resolveAnchorQuickPickButtonLabel({
			startDate,
			endDate,
			label: anchor.label,
			months: anchor.months,
			quarters: anchor.quarters,
			periodAxis: anchor.periodAxis,
			periods: anchor.periods,
		});
		if (quickPickLabel) return quickPickLabel;
	}
	return formatDateRangeLabel(startDate, endDate);
};

/** 下单日期标签：与选期按钮一致，优先展示按月/按季元数据 */
export const resolveStatisticsCreatedDateRangeLabel = (startDate, endDate, trendAnchor = null) => {
	if (!startDate && !endDate) return '';
	const anchor = trendAnchor;
	if (anchor?.startDate && anchor?.endDate && trendAnchorMatchesOrderRange(anchor, startDate, endDate)) {
		const quickPickLabel = resolveAnchorQuickPickButtonLabel({
			startDate,
			endDate,
			label: anchor.label,
			months: anchor.months,
			quarters: anchor.quarters,
			periodAxis: anchor.periodAxis,
			periods: anchor.periods,
		});
		if (quickPickLabel) return quickPickLabel;
	}
	return formatDateRangeLabel(startDate, endDate);
};

export const normalizeEntityId = (value) => {
	if (value == null || value === '') return '';
	if (typeof value === 'string') return value;
	if (typeof value === 'object') {
		if (value._id != null) return String(value._id);
		if (typeof value.toString === 'function' && value.toString() !== '[object Object]') {
			return value.toString();
		}
	}
	return String(value);
};

const resolveUserLabel = (userId, users = []) => users.find((user) => normalizeEntityId(user._id) === normalizeEntityId(userId))?.name ?? userId;

const resolveOptionLabel = (value, options = []) =>
	options.find((option) => normalizeEntityId(option.value) === normalizeEntityId(value))?.label ?? normalizeEntityId(value);

const hasArrayValue = (value) => Array.isArray(value) && value.length > 0;

/** 业务统计已生效筛选 → 查询标签 */
export const buildStatisticsFilterTags = (
	filters = {},
	{
		saleUsers = [],
		assistantUsers = [],
		customerOptions = [],
		customerContactOptions = [],
		productOptions = [],
		provinceOptions = [],
		cityOptions = [],
	} = {},
) => {
	const tags = [];
	const normalized = { ...STATISTICS_BASE_FILTER_DEFAULTS, ...filters };

	if ((normalized.startDate || normalized.endDate) && !isDefaultThisYearOrderRange(normalized.startDate, normalized.endDate)) {
		tags.push({
			id: 'createdDateRange',
			label: `下单日期：${resolveStatisticsCreatedDateRangeLabel(
				normalized.startDate,
				normalized.endDate,
				normalized.trendAnchor,
			)}`,
			/** 清掉非标题档选期：交回当年 */
			patch: buildReleaseOverviewPeriodToYearPatch(),
		});
	}

	if (normalized.completedStartDate || normalized.completedEndDate) {
		tags.push({
			id: 'completedDateRange',
			label: `完成日期：${resolveStatisticsCompletedDateRangeLabel(
				normalized.completedStartDate,
				normalized.completedEndDate,
				normalized.completedDateAnchor,
			)}`,
			patch: { completedStartDate: null, completedEndDate: null, completedDateAnchor: null },
		});
	}

	if (
		normalized.dateRelation === 'or' &&
		(normalized.startDate || normalized.endDate) &&
		(normalized.completedStartDate || normalized.completedEndDate)
	) {
		tags.push({
			id: 'dateRelation',
			label: '日期关系：满足其一（或）',
			patch: { dateRelation: 'and' },
		});
	}

	if (hasArrayValue(normalized.saleUser)) {
		tags.push({
			id: 'saleUser',
			label: `销售员：${formatMultiValueLabel(normalized.saleUser.map((userId) => resolveUserLabel(userId, saleUsers)))}`,
			patch: { saleUser: [] },
		});
	}

	if (hasArrayValue(normalized.assistantUser)) {
		tags.push({
			id: 'assistantUser',
			label: `助理：${formatMultiValueLabel(normalized.assistantUser.map((userId) => resolveUserLabel(userId, assistantUsers)))}`,
			patch: { assistantUser: [] },
		});
	}

	if (hasArrayValue(normalized.primaryPhases)) {
		tags.push({
			id: 'primaryPhases',
			label: `流程节点：${formatMultiValueLabel(normalized.primaryPhases.map(getOrderPrimaryPhaseLabel))}`,
			patch: { primaryPhases: [] },
		});
	}

	if (hasArrayValue(normalized.suppliers)) {
		tags.push({
			id: 'suppliers',
			label: `厂家：${formatMultiValueLabel(normalized.suppliers)}`,
			patch: { suppliers: [] },
		});
	}

	if (hasArrayValue(normalized.brands)) {
		tags.push({
			id: 'brands',
			label: `品牌：${formatMultiValueLabel(normalized.brands)}`,
			patch: { brands: [] },
		});
	}

	if (hasArrayValue(normalized.categories)) {
		tags.push({
			id: 'categories',
			label: `品类：${formatMultiValueLabel(normalized.categories)}`,
			patch: { categories: [] },
		});
	}

	if (hasArrayValue(normalized.products)) {
		tags.push({
			id: 'products',
			label: `产品：${formatMultiValueLabel(normalized.products.map((id) => resolveOptionLabel(id, productOptions)), 1)}`,
			patch: { products: [] },
		});
	}

	if (hasArrayValue(normalized.customers)) {
		tags.push({
			id: 'customers',
			label: `单位：${formatMultiValueLabel(normalized.customers.map((id) => resolveOptionLabel(id, customerOptions)), 1)}`,
			patch: { customers: [] },
		});
	}

	if (hasArrayValue(normalized.customerProvince)) {
		tags.push({
			id: 'customerProvince',
			label: `省：${formatMultiValueLabel(normalized.customerProvince.map((code) => resolveOptionLabel(code, provinceOptions)))}`,
			patch: { customerProvince: [], customerCity: [] },
		});
	}

	if (hasArrayValue(normalized.customerCity)) {
		tags.push({
			id: 'customerCity',
			label: `市：${formatMultiValueLabel(normalized.customerCity.map((code) => resolveOptionLabel(code, cityOptions)))}`,
			patch: { customerCity: [] },
		});
	}

	if (hasArrayValue(normalized.customerContacts)) {
		tags.push({
			id: 'customerContacts',
			label: `联系人：${formatMultiValueLabel(
				normalized.customerContacts.map((id) => resolveOptionLabel(id, customerContactOptions)),
				1,
			)}`,
			patch: { customerContacts: [] },
		});
	}

	return tags;
};

/** 折线对比已生效配置 → 图表区标签（与业务筛选标签分开展示） */
export const buildStatisticsLineCompareTags = (filters = {}) => {
	const tags = [];
	const normalized = { ...STATISTICS_BASE_FILTER_DEFAULTS, ...filters };

	for (const period of normalized.customComparePeriods ?? []) {
		if (!period?.startDate || !period?.endDate) continue;
		const rangeLabel = resolveComparePeriodChipLabel({
			startDate: period.startDate,
			endDate: period.endDate,
			label: period.label,
			periodAxis: period.periodAxis ?? normalized.trendAnchor?.periodAxis,
			months: period.months,
			quarters: period.quarters,
		});
		tags.push({
			id: `compare-${period.id}`,
			label: `折线对比：${rangeLabel}`,
			patch: {
				customComparePeriods: (normalized.customComparePeriods ?? []).filter((item) => item.id !== period.id),
			},
		});
	}

	const lineAnchor = normalized.trendAnchor;
	if (lineAnchor?.startDate && lineAnchor?.endDate) {
		const anchorLabel = lineAnchor.label?.trim() || formatComparePeriodLabel(lineAnchor.startDate, lineAnchor.endDate);
		tags.push({
			id: 'trendAnchor',
			label: `折线基准：${anchorLabel}`,
			patch: { trendAnchor: null },
		});
	}

	return tags;
};

export const hasStatisticsLineCompareValues = (filters = {}) => buildStatisticsLineCompareTags(filters).length > 0;

const STATISTICS_COMMON_FILTER_TAG_IDS = new Set(['createdDateRange', 'completedDateRange', 'dateRelation']);

/** 常用筛选（收起态展示）已生效标签 */
export const buildStatisticsCommonFilterTags = (filters = {}, context = {}) =>
	buildStatisticsFilterTags(filters, context).filter((tag) => STATISTICS_COMMON_FILTER_TAG_IDS.has(tag.id));

/** 高级筛选已生效标签 */
export const buildStatisticsAdvancedFilterTags = (filters = {}, context = {}) =>
	buildStatisticsFilterTags(filters, context).filter((tag) => !STATISTICS_COMMON_FILTER_TAG_IDS.has(tag.id));

export const hasStatisticsAdvancedFilterValues = (filters = {}, context = {}) =>
	buildStatisticsAdvancedFilterTags(filters, context).length > 0;

export const hasStatisticsFilterValues = (filters = {}) => buildStatisticsFilterTags(filters).length > 0;
